import React, { useState, useRef, useEffect } from 'react';
import MathInput from "@/components/Math/math-input";
import MathField from "@/components/Math/math-field";
import MarkdownComponent from "../Markdown";
import { colors } from "@/constant/colors";
import { isEquivalentMathExpression } from "@/lib/utils";
import ExpandExplanation from "../PracticeCard/expandExplanation";
import Image from "@/components/Image";
import NormalButton from "../Button/normal";
import { 
  MathFillingProps, 
  AnswerState, 
  AnswerCallback, 
  InputChangeCallback,
  MathFillingState 
} from "./types";

const MathFilling: React.FC<MathFillingProps> = ({ 
  question, 
  userAnswer = '',
  answerState = "none",
  readonly = false,
  hideQuestion = false, 
  needButtons = true,
  showExplanation = true,
  onAnswer,
  onInputChange,
  triggerCheck = false
}) => {
  const [isSubmit, setIsSubmit] = useState(false);
  const [answer, setAnswer] = useState(userAnswer);
  const alertRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // 当外部 userAnswer 变化时更新内部状态
  useEffect(() => {
    if (userAnswer !== answer) {
      setAnswer(userAnswer);
      setIsSubmit(userAnswer !== '');
    }
  }, [userAnswer]);

  const handleSubmit = () => {
    if (answer === '') return;
    setIsSubmit(true);
    const isCorrect = isEquivalentMathExpression(answer, question.answer);
    onAnswer?.(answer, isCorrect, true); // autoSubmit = true
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isSubmit && !readonly) {
      handleSubmit();
    }
  }

  // 响应外部的Check触发
  useEffect(() => {
    if (triggerCheck && !isSubmit && answer !== '') {
      handleSubmit();
    }
  }, [triggerCheck]);

  // 当输入变化时通知父组件
  const handleAnswerChange = (value: string) => {
    setAnswer(value);
    onInputChange?.(value);
  };

  const handleIDontKnow = () => {
    setIsSubmit(true);
    onAnswer?.(answer, false, false);
  }

  const isAnswered = answerState !== "none" || isSubmit;
  const isCorrect = answerState === "correct" || (isSubmit && isEquivalentMathExpression(answer, question.answer));

  return (
    <div className="h-full w-full flex flex-col items-center justify-center" onKeyDown={handleKeyDown}>
      {
        !hideQuestion && (
          <div className="max-w-[800px] px-4 text-xl text-[#573F35] text-center pb-4 emphasize-text">
            <MarkdownComponent 
              content={question?.question} 
              textStyle={{ 
                textAlign: 'center', 
                fontFamily: 'var(--font-emphasize)', 
                color: colors.mainTextColor, 
                fontSize: '20px', 
                fontWeight: '700' 
              }} 
            />
          </div>
        )
      }
      {
        isAnswered ? (
          <div className="flex flex-col w-full items-center">
            <div className="w-full px-4">
              <span className="emphasize-text text-[1.25rem]">
                Your Answer: <MathField 
                  latex={answer} 
                  color={isCorrect ? colors.correctAnswerColor : colors.wrongAnswerColor} 
                />
              </span>
              {showExplanation && (
                <ExpandExplanation 
                  explanation={question?.explanation || ''} 
                  initialHideExplanation={isCorrect} 
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-15 max-w-[800px] w-full">
            <div className="relative flex flex-row gap-2">
              <div className="flex flex-col items-center justify-center">
                <div className="w-[300px] md:w-[400px]" ref={alertRef}>
                  <MathInput
                    value={answer}
                    onChange={handleAnswerChange}
                  />
                </div>
                {
                  needButtons && (
                    <div className="w-[300px] md:w-[400px] h-[50px] flex flex-row gap-2 mt-4">
                      <div className="w-full">
                        <NormalButton 
                          colorType="gray" 
                          onClick={readonly ? () => {} : handleIDontKnow}
                        >
                          I don't know
                        </NormalButton>
                      </div>
                      <div className="w-full">
                        <NormalButton 
                          onClick={readonly ? () => {} : handleSubmit}
                        >
                          Submit
                        </NormalButton>
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
          </div>
        )
      }
    </div>
  )
}

export default MathFilling;
